<script>
  import VoiceRecorder from './VoiceRecorder.svelte';
  import { MAX_WIZARD_SELECTIONS, wizardAnswerValues } from '../utils/wizard-progress.js';

  let { question, index, answer, active = false, disabled = false, refreshing = false,
    narrationEnabled = false, narrationLoading = false, anotherRecorderBusy = false,
    onFocus, onToggle, onCustomInput, onSkip, onRefresh, onListen, onEnter, onRecordingChange } = $props();
  let showDescriptions = $state(false);
  let voiceState = $state('idle');
  let voiceDuration = $state(0);
  let triggerVoiceStop = $state(false);
  let triggerVoiceCancel = $state(false);
  const selected = $derived(new Set(answer.selectedOptions));
  const values = $derived(wizardAnswerValues(answer));

  function recordingChanged({ state, duration }) {
    voiceState = state;
    voiceDuration = duration;
    if (state === 'idle') {
      triggerVoiceStop = false;
      triggerVoiceCancel = false;
    } else onFocus();
    onRecordingChange(state);
  }
</script>

<section class="question-card" class:active class:skipped={answer.skipped}
  aria-labelledby={`wizard-question-${index}`} onfocusin={onFocus}>
  <div class="card-heading">
    <span class="question-label">Question {index + 1}</span>
    {#if narrationEnabled}
      <button type="button" class="text-action" onclick={onListen}
        disabled={disabled || narrationLoading || voiceState !== 'idle' || anotherRecorderBusy}
        aria-label={`Listen to question ${index + 1}`}>
        {narrationLoading ? 'Loading voice…' : 'Listen'}
      </button>
    {/if}
  </div>
  <h2 id={`wizard-question-${index}`} tabindex="-1">{question.question}</h2>

  <div class="presets" aria-busy={refreshing}>
    {#each question.presets as preset, presetIndex}
      {@const value = typeof preset === 'object' ? preset.description : preset}
      {@const title = typeof preset === 'object' ? preset.title : preset}
      <button type="button" class="preset" class:selected={selected.has(value)}
        aria-pressed={selected.has(value)} disabled={disabled || refreshing || voiceState !== 'idle'}
        onclick={() => onToggle(value)}>
        <span class="preset-number" aria-hidden="true">{selected.has(value) ? '✓' : presetIndex + 1}</span>
        <span class="preset-text"><span>{title}</span>
          {#if showDescriptions && title !== value}<span class="description">{value}</span>{/if}
        </span>
      </button>
    {/each}
  </div>

  <div class="card-tools">
    <button type="button" class="text-action" onclick={() => showDescriptions = !showDescriptions}
      disabled={disabled} aria-expanded={showDescriptions}>{showDescriptions ? 'Hide details' : 'Option details'}</button>
    <button type="button" class="text-action" onclick={onRefresh}
      disabled={disabled || refreshing || voiceState !== 'idle'}>{refreshing ? 'Loading…' : 'More options'}</button>
    <button type="button" class="text-action" onclick={onSkip}
      disabled={disabled || refreshing || voiceState !== 'idle'} aria-pressed={answer.skipped}>
      {answer.skipped ? 'Skipped ✓' : 'Skip'}
    </button>
  </div>

  <label for={`wizard-answer-${index}`}>Or your own answer</label>
  <div class="answer-input">
    {#if voiceState === 'recording'}
      <button type="button" class="recording-action" onclick={() => triggerVoiceCancel = true}
        aria-label={`Cancel recording for question ${index + 1}`}>Cancel</button>
      <span class="recording-time" role="status">Recording {voiceDuration}s</span>
      <button type="button" class="recording-action" onclick={() => triggerVoiceStop = true}
        aria-label={`Transcribe answer for question ${index + 1}`}>Done</button>
    {:else if voiceState === 'starting'}
      <span class="recording-time" role="status">Starting microphone…</span>
      <button type="button" class="recording-action" onclick={() => triggerVoiceCancel = true}>Cancel</button>
    {:else if voiceState === 'processing'}
      <span class="recording-time" role="status">Transcribing…</span>
    {:else}
      <input id={`wizard-answer-${index}`} value={answer.customInput} maxlength="200"
        placeholder="Type or speak…" disabled={disabled || refreshing || anotherRecorderBusy}
        oninput={event => onCustomInput(event.target.value)}
        onkeydown={event => {
          if (event.key === 'Enter' && answer.customInput.trim()) { event.preventDefault(); onEnter(); }
        }} />
    {/if}
    <div class="answer-voice" class:hidden={voiceState === 'recording'}>
      <VoiceRecorder compact={true} onStateChange={recordingChanged}
        onTranscript={onCustomInput} disabled={disabled || refreshing || anotherRecorderBusy}
        externalStop={triggerVoiceStop} externalCancel={triggerVoiceCancel} />
    </div>
  </div>
  <p class="answer-status" role="status">
    {#if answer.skipped}Keep the original idea for this question.
    {:else if answer.customInput.trim()}Custom answer ready
    {:else if values.length}{values.length} option{values.length > 1 ? 's' : ''} selected
    {:else}Choose one or more, enter an answer, or skip.{/if}
    {#if values.length === MAX_WIZARD_SELECTIONS} · All six selected{/if}
  </p>
</section>

<style>
  .question-card {
    min-width: 0; padding: 1.1rem; background: var(--color-surface);
    border: 1px solid var(--color-border); border-radius: 16px; text-align: left;
  }
  .question-card.active { border-color: var(--color-primary); }
  .card-heading { display: flex; justify-content: space-between; align-items: center; gap: 8px; min-height: 36px; }
  .question-label { color: var(--color-text-muted); font-size: 0.8rem; font-weight: 600; }
  h2 { font-size: 1.1rem; line-height: 1.4; margin: 0.25rem 0 0.85rem; overflow-wrap: anywhere; }
  .presets { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.4rem; }
  .preset { display: flex; align-items: center; gap: 6px; min-height: 44px; text-align: left;
    border: 1px solid var(--color-border); border-radius: 9px; padding: 8px;
    color: var(--color-text); background: var(--color-bg); font: inherit; font-size: 0.85rem; }
  .preset.selected { color: var(--color-primary); border-color: var(--color-primary); background: var(--color-primary-light); }
  .preset:focus-visible, .text-action:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
  .preset-number { font-size: 0.8rem; color: var(--color-text-muted); width: 14px; flex: 0 0 14px; }
  .preset-text { min-width: 0; overflow-wrap: anywhere; }
  .description { display: block; font-size: 0.75rem; color: var(--color-text-muted); margin-top: 0.3rem; }
  .card-tools { display: flex; justify-content: space-between; gap: 4px; flex-wrap: wrap; margin-top: 0.4rem; }
  .text-action { padding: 5px 2px; min-height: 44px; color: var(--color-primary); background: transparent; border: 0;
    font: inherit; font-size: 0.78rem; cursor: pointer; }
  label { display: block; margin: 0.4rem 0; font-size: 0.8rem; color: var(--color-text-muted); }
  .answer-input { display: flex; align-items: center; min-height: 44px; gap: 4px; }
  input { flex: 1; min-width: 0; width: 0; margin: 0; font-size: 0.9rem; min-height: 44px; }
  .answer-voice { flex: 0 0 40px; }
  .answer-voice.hidden { display: none; }
  .recording-time { flex: 1; font-size: 0.8rem; }
  .recording-action { min-height: 44px; padding: 6px 10px; font-size: 0.8rem; }
  .answer-status { margin: 0.4rem 0 0; min-height: 1.1em; font-size: 0.75rem; color: var(--color-text-muted); }
  button:disabled { cursor: default; opacity: 0.6; }
  @media (max-width: 400px) { .question-card { padding: 0.85rem; } }
</style>
